import { CloseOutlined, DeleteOutlined, DownOutlined, EditOutlined, PlusOutlined, UpOutlined } from '@ant-design/icons';
import { Form, FormInstance, FormListFieldData, Input, Typography } from 'antd';
import Add from 'assets/Icons/add';
import Button from 'components/Button';
import colorTheme from 'constants/color';
import { ChevronDown, ChevronUp, SquarePen, Trash } from 'lucide-react';
import React, { useState } from 'react';
import styled from 'styled-components';

interface ConfigCardProps {
  field: FormListFieldData;
  form: FormInstance;
  remove: (index: number) => void;
  onSelectService: () => void;
  defaultOpen: boolean;
}

const COLORS = [colorTheme.teal_4, colorTheme.coral_5, colorTheme.gold_5, colorTheme.ink_2];

const ConfigCard = ({ field, form, remove, onSelectService, defaultOpen }: ConfigCardProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const services = Form.useWatch(['config', field.name, 'services'], form) || [];

  return (
    <Container className={`${open ? 'active' : ''}`}>
      <div className='config-header' onClick={() => setOpen(!open)}>
        <LeftLine color={COLORS[field.name % COLORS.length]} />

        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Form.Item name={[field.name, 'code']} rules={[{ required: true, message: 'Code is required' }]}>
            <EditableText />
          </Form.Item>
        </div>

        <div className='preview'>
          {services.length
            ? services
                .slice(0, 8)
                .map((x: any) => x.serviceName)
                .join(' · ')
            : 'No services assigned'}
        </div>

        <div className='right'>
          <Button
            ntype='LIGHT_PRIMARY'
            height={3}
            newStyle
            iconElement={<Trash strokeWidth={2} color={colorTheme.coral_6} />}
            onClick={(e) => {
              e.stopPropagation();
              remove(field.name);
            }}
          >
            Delete
          </Button>
          <div className='count'>{services.length}</div>

          <ChevronDown className={`arrow ${open ? 'open' : ''}`} />
        </div>
      </div>

      <div className={`config-body ${open ? 'open' : ''}`}>
        <div className='service-list'>
          {services.map((service: any, index: number) => (
            <div className='service-item' key={service.id}>
              {service.serviceName}

              <div
                className='delete-icon'
                onClick={(e) => {
                  e.stopPropagation();
                  const newServices = services.filter((_: any, i: number) => i !== index);

                  form.setFieldValue(['config', field.name, 'services'], newServices);
                }}
              >
                <CloseOutlined style={{ fontSize: '16px' }} />
              </div>
            </div>
          ))}

          <Button
            ntype='LIGHT_BLUE'
            newStyle
            height={3}
            iconElement={<Add fill={colorTheme.teal_6} />}
            onClick={onSelectService}
          >
            Add Service
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default ConfigCard;

const Container = styled.div`
  border: 1px solid ${colorTheme.ink_2};
  border-radius: 18px;
  margin-bottom: 16px;
  background: #fff;
  transition: 0.25s;

  &.active {
    border-color: ${colorTheme.teal_4};
    box-shadow: 0 0 0 1px ${colorTheme.teal_4};
  }
  .config-header {
    display: flex;
    align-items: center;
    padding: 18px 22px;
    cursor: pointer;
    gap: 16px;
  }

  .code {
    width: 140px;
    font-weight: 700;
    font-size: 20px;
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 0;
    border: none;
    border-bottom: 2px solid transparent;
  }

  .ant-typography-edit-content .ant-input {
    border: none;
    border-bottom: 2px solid ${colorTheme.teal_4};
    border-radius: 0;
    box-shadow: none;
    font-size: 20px;
    font-weight: 700;
  }

  .ant-typography-edit {
    display: flex !important;
    align-items: center;
    justify-content: center;
    color: ${colorTheme.teal_4};
  }

  .preview {
    flex: 1;
    color: ${colorTheme.ink_9};
    overflow: hidden;
    white-space: nowrap;
    font-weight: 500;
    text-overflow: ellipsis;
    font-size: 18px;
    font-family: Poppins;
  }

  .right {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: 16px;
  }

  .count {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${colorTheme.teal_4};
    color: white;

    display: flex;
    justify-content: center;
    align-items: center;
    font-family: Poppins;

    font-weight: 600;
  }

  .config-body {
    padding: 0 24px;
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.28s ease;
  }

  .config-body > div {
    overflow: hidden;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .config-body.open {
    border-top: 1px solid #ececec;
    padding: 24px;
    grid-template-rows: 1fr;
  }

  .config-body.open > div {
    opacity: 1;
  }

  .service-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .service-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 18px;
    border: 1px solid ${colorTheme.ink_2};
    border-radius: 999px;
    background: ${colorTheme.ink_1};
    font-family: Poppins;
    font-size: 18px;
    height: 3rem;
    box-sizing: border-box;
  }

  .delete-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: ${colorTheme.v2_neutral_1};
    border-radius: 50%;
    cursor: pointer;
  }

  .arrow {
    transition: transform 0.3s ease;
  }

  .arrow.open {
    transform: rotate(180deg);
  }
`;

const LeftLine = styled.div<{ color: string }>`
  width: 6px;
  height: 40px;
  background: ${({ color }) => color};
  border-radius: 999px;
`;

interface EditableTextProps {
  value?: string;
  onChange?: (value: string) => void;
}

const EditableText = ({ value, onChange }: EditableTextProps) => {

  return (
    <Typography.Text
      className='code'
      editable={{
        icon: <SquarePen strokeWidth={2.5} />,
        onChange: async (value) => {
          onChange?.(value);
        },
      }}
    >
      {value}
    </Typography.Text>
  );
};
